import * as React from 'react';
import { Link } from 'react-router-dom';
import {
	floatToPercentageString,
	getObjectChildrenKeyIds,
	getSpendSourceDrillDownLink,
} from '../../utils/DataFormatterUtils';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { SpendSourceDescriptor } from '../../utils/AnalyticsUtils';
import { INVOICE_STATUS_DISPLAY_NAMES, INVOICE_STATUSES } from '../../constants/InvoiceStatuses';

// Invoice drill-downs filter to the same statuses the spend_reportings invoice slice counts.
const APPROVED_INVOICE_STATUSES = [
	INVOICE_STATUS_DISPLAY_NAMES[INVOICE_STATUSES.approved],
	INVOICE_STATUS_DISPLAY_NAMES[INVOICE_STATUSES.processing],
	INVOICE_STATUS_DISPLAY_NAMES[INVOICE_STATUSES.paid],
];

type BuildSpendBreakdownColumnsArgs = {
	activeSources: SpendSourceDescriptor[];
	currency: any;
	userType: string;
	// The tab's base filters (problemTypeFilters / locationFilters / vendorFilters).
	baseFilters: any;
	// Which filter key the row's dimension ids map to on the drill-down target.
	dimensionFilterKey: string;
	// Tree rows carry amounts under `spendCosts`; flat rows (vendors) carry them directly.
	fromTree: boolean;
};

const readCosts = (record, fromTree) =>
	fromTree ? nullSafeGetOrElse('spendCosts', record, {}) : record;

// Builds the per-source spend columns + Total + % of Total for the spend breakdown tables (OPE-4875).
// Each source value links to its own drill-down (invoice → invoices list, others → work-orders list).
// Zero values render as plain, non-linked text.
export const buildSpendBreakdownColumns = ({
	activeSources,
	currency,
	userType,
	baseFilters,
	dimensionFilterKey,
	fromTree,
}: BuildSpendBreakdownColumnsArgs) => {
	const sourceColumns = activeSources.map((source) => ({
		title: source.label,
		key: source.key,
		width: 130,
		render: (_text, record) => {
			const costs = readCosts(record, fromTree);
			const amount = parseFloat(nullSafeGetOrElse(source.key, costs, 0)) || 0;
			if (!amount) {
				return <span className="text-grey-6">{currency.symbolFormat(0, 0)}</span>;
			}
			const filters = {
				...baseFilters,
				[dimensionFilterKey]: getObjectChildrenKeyIds(record),
			};
			if (source.key === 'invoiceSpend') {
				filters.statuses = APPROVED_INVOICE_STATUSES;
			}
			return (
				<Link
					to={getSpendSourceDrillDownLink(userType, filters, source.key)}
					target="_blank"
					rel="noopener noreferrer"
					className="inline-flex items-center"
				>
					{/* Keep the amount in the default text color; only the external-link icon
					    takes the link (primary) color. inline-flex + items-center keeps the icon
					    vertically centered with the amount. */}
					<span className="text-character-primary">{currency.symbolFormat(amount, 0)}</span>
					<i className="icons8-font icons8-external-link ml-2" />
				</Link>
			);
		},
	}));

	const totalColumn = {
		title: `Total (${currency.id})`,
		key: 'totalSpend',
		defaultSortOrder: 'descend' as 'ascend' | 'descend',
		sorter: (a, b) =>
			(parseFloat(nullSafeGetOrElse('totalSpend', readCosts(a, fromTree), 0)) || 0) -
			(parseFloat(nullSafeGetOrElse('totalSpend', readCosts(b, fromTree), 0)) || 0),
		render: (_text, record) =>
			currency.symbolFormat(
				parseFloat(nullSafeGetOrElse('totalSpend', readCosts(record, fromTree), 0)) || 0,
				0
			),
		width: 140,
	};

	// With a single source (e.g. the invoice-only Suppliers tab) the source column already IS the
	// total, so drop the redundant Total column and base % on that source's own percentage field.
	const isSingleSource = activeSources.length === 1;
	const percentageKey = isSingleSource ? activeSources[0].percentageKey : 'percentageOfTotalSpend';

	const percentageColumn = {
		title: '% of Total',
		key: percentageKey,
		render: (_text, record) =>
			floatToPercentageString(
				(parseFloat(nullSafeGetOrElse(percentageKey, readCosts(record, fromTree), 0)) || 0) / 100
			),
		width: 100,
	};

	return [...sourceColumns, ...(isSingleSource ? [] : [totalColumn]), percentageColumn];
};
